import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Triage.css';

const Triage = () => {
  const { 
    patients, 
    surveyTemplates, 
    surveyAssignments, 
    fetchSurveyAssignments,
    currentUser
  } = useAuth();
  const [filteredResponses, setFilteredResponses] = useState([]);
  
  // Fetch survey assignments when component mounts or patients change
  useEffect(() => {
    if (currentUser) {
      console.log('🔍 Fetching survey assignments for clinician...');
      fetchSurveyAssignments(currentUser.uid, true);
    }
  }, [currentUser, patients.length]); // Re-run if patients array changes
  const [filters, setFilters] = useState({
    status: 'all',
    surveyType: 'all',
    patient: 'all',
    dateRange: 'all',
    showReviewed: false
  });
  const navigate = useNavigate();

  // Process survey responses when component mounts or data changes
  useEffect(() => {
    console.log('=== TRIAGE COMPONENT UPDATED ===');
    console.log('Survey assignments from context:', surveyAssignments);
    console.log('Patients from context:', patients);
    console.log('Survey templates from context:', surveyTemplates);
    
    console.log('Processing survey responses...', { 
      assignments: surveyAssignments.length,
      patients: patients.length,
      templates: surveyTemplates.length 
    });
    
    const responses = [];
    
    if (surveyAssignments.length === 0) {
      console.warn('No survey assignments found. This could be because:');
      console.warn('1. No assignments exist in the database');
      console.warn('2. The current user does not have permission to view any assignments');
      console.warn('3. There was an error fetching the assignments');
    }
    
    // Filter responses based on showReviewed filter
    const assignmentsToProcess = surveyAssignments.filter(assignment => {
      // If showReviewed is true, only include reviewed responses
      // If showReviewed is false, only include non-reviewed responses
      return filters.showReviewed 
        ? assignment.isReviewed === true 
        : assignment.isReviewed !== true;
    });
    
    console.log(`Processing ${assignmentsToProcess.length} assignments (${filters.showReviewed ? 'including' : 'excluding'} reviewed)`);
    
    // Loop through filtered survey assignments
    assignmentsToProcess.forEach((assignment, index) => {
      try {
        // Debug log for each assignment
        console.log(`\n--- Processing assignment ${index + 1}/${surveyAssignments.length} ---`);
        console.log('Assignment details:', { 
          id: assignment.id,
          status: assignment.status,
          hasResponses: !!assignment.responses,
          patientId: assignment.patientId,
          surveyId: assignment.surveyId,
          completedDate: assignment.completedDate,
          assignedDate: assignment.assignedDate,
          responses: Object.keys(assignment.responses || {}).length
        });
        
        if (assignment.status === 'completed' && assignment.responses) {
          const patient = patients.find(p => p.id === assignment.patientId);
          const template = surveyTemplates.find(t => t.id === assignment.surveyId);
          
          // Debug log for found patient and template
          console.log('Found patient and template:', { 
            patientFound: !!patient, 
            templateFound: !!template,
            patientName: patient?.name,
            templateName: template?.name || template?.title
          });
          
          if (patient) {
            responses.push({
              id: assignment.id,
              ...assignment,
              patientName: patient.name || 'Unknown Patient',
              patientId: patient.id,
              surveyName: template?.name || template?.title || assignment.surveyTitle || 'Untitled Survey',
              surveyTitle: template?.title || assignment.surveyTitle || 'Untitled Survey',
              completedDate: assignment.completedDate || assignment.updatedAt || new Date().toISOString(),
              // Include all responses and question data
              responses: assignment.responses || {},
              // Include the template for question data if available
              template: template || null
            });
          } else {
            console.warn('Patient not found for assignment:', assignment.id);
          }
        }
      } catch (error) {
        console.error('Error processing assignment:', { 
          assignmentId: assignment.id, 
          error: error.message 
        });
      }
    });

    // Apply filters
    let result = [...responses];
    
    if (filters.surveyType !== 'all') {
      result = result.filter(r => r.surveyId === filters.surveyType);
    }
    
    if (filters.patient !== 'all') {
      result = result.filter(r => r.patientId === filters.patient);
    }
    
    if (filters.dateRange !== 'all') {
      const today = new Date();
      const cutoff = new Date();
      
      switch(filters.dateRange) {
        case 'today':
          cutoff.setHours(0, 0, 0, 0);
          break;
        case 'week':
          cutoff.setDate(today.getDate() - 7);
          break;
        case 'month':
          cutoff.setMonth(today.getMonth() - 1);
          break;
        default:
          break;
      }
      
      result = result.filter(r => new Date(r.completedDate) > cutoff);
    }
    
    // Sort by completion date (newest first)
    result.sort((a, b) => new Date(b.completedDate) - new Date(a.completedDate));
    
    setFilteredResponses(result);
  }, [surveyAssignments, patients, surveyTemplates, filters]);

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const viewResponse = (response) => {
    if (response && response.id) {
      // Navigate to the survey response detail page
      navigate(`/survey-response/${response.id}`);
    }
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getQuestionCount = (responses) => {
    if (!responses) return 0;
    return Object.keys(responses).length;
  };

  return (
    <div className="triage-container">
      <div className="triage-header">
        <h2>Survey Responses Triage</h2>
        <p>Review and manage all submitted survey responses</p>
      </div>
      
      <div className="filters">
        <div className="filter-group">
          <label>Survey Type</label>
          <select name="surveyType" value={filters.surveyType} onChange={handleFilterChange}>
            <option value="all">All Surveys</option>
            {surveyTemplates.map(survey => (
              <option key={survey.id} value={survey.id}>
                {survey.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label>Patient</label>
          <select name="patient" value={filters.patient} onChange={handleFilterChange}>
            <option value="all">All Patients</option>
            {patients.map(patient => (
              <option key={patient.id} value={patient.id}>
                {patient.name}
              </option>
            ))}
          </select>
        </div>
        
        <div className="col-md-3">
          <label className="form-label">Date Range</label>
          <select 
            className="form-select" 
            name="dateRange" 
            value={filters.dateRange}
            onChange={handleFilterChange}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>
        <div className="col-md-3 d-flex align-items-end">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id="showReviewed"
              name="showReviewed"
              checked={filters.showReviewed}
              onChange={handleFilterChange}
            />
            <label className="form-check-label" htmlFor="showReviewed">
              Show Reviewed Responses
            </label>
          </div>
        </div>
      </div>
      
      <div className="responses-table-container">
        {filteredResponses.length > 0 ? (
          <table className="responses-table">
            <thead>
              <tr>
                <th>Survey</th>
                <th>Patient</th>
                <th>Completed</th>
                <th>Questions</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredResponses.map((response, index) => (
                <tr 
                  key={response.id} 
                  className={`table-row ${index % 2 === 0 ? 'even' : 'odd'} clickable-row`}
                  onClick={(e) => {
                    // Only navigate if the click wasn't on a button or link
                    if (!e.target.closest('button, a, [role="button"]')) {
                      viewResponse(response);
                    }
                  }}
                  role="row"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      viewResponse(response);
                    }
                  }}
                >
                  <td>
                    <div className="survey-info">
                      <div className="survey-name">{response.surveyTitle || response.surveyName}</div>
                      <div className="survey-type">{response.surveyName}</div>
                    </div>
                  </td>
                  <td>{response.patientName}</td>
                  <td>{formatDate(response.completedDate)}</td>
                  <td>{getQuestionCount(response.responses)}</td>
                  <td>
                    <span className={`status-badge ${response.status}`}>
                      {response.status}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="view-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        viewResponse(response);
                      }}
                      aria-label={`View details for ${response.patientName}'s ${response.surveyName} survey`}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="no-results">
            <p>No survey responses found matching your filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Triage;
